<template>
  <Teleport to="body">
    <div v-if="visible" class="country-modal-overlay" @click.self="$emit('close')">
      <div class="country-modal">
        <div class="country-modal__header">
          <div class="country-modal__search-wrapper">
            <svg class="country-modal__search-icon" viewBox="0 0 24 24" fill="none">
              <circle cx="11"
cy="11"
r="7"
stroke="currentColor"
stroke-width="2"/>
              <path d="M21 21l-4.35-4.35"
stroke="currentColor"
stroke-width="2"
stroke-linecap="round"/>
            </svg>
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="country-modal__search-input"
              :placeholder="$t('common.search')"
              autocomplete="off"
            />
          </div>
        </div>

        <div class="country-modal__list">
          <button
            v-for="country in filteredCountries.slice(0, visibleCount)"
            :key="country.code"
            :class="[
              'country-item',
              { 'country-item--selected': selectedCode === country.code }
            ]"
            @click="selectCountry(country)"
          >
            <img
              :src="getFlagUrl(country.code)"
              :alt="t(`countries.${country.code}`)"
              class="country-item__flag"
              loading="lazy"
              @error="onFlagError"
            />
            <span class="country-item__name">{{ t(`countries.${country.code}`) }}</span>
          </button>

          <div
            v-if="filteredCountries.length > visibleCount"
            ref="loadMoreTrigger"
            class="country-modal__load-more"
          >
            {{ $t('common.countriesLoading') }}
          </div>

          <div v-if="filteredCountries.length === 0" class="country-modal__empty">
            {{ $t('common.countriesNotFound') }}
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { COUNTRIES, type CountryOption } from '@/modules/profile/data/countries'
import { useProfileStore } from '@/modules/profile/store/profileStore'

const { t } = useI18n()

const props = defineProps<{
  visible: boolean
}>()

const emit = defineEmits<{
  (e: 'select', payload: { code: string; name: string }): void
  (e: 'close'): void
}>()

const profileStore = useProfileStore()

const searchQuery = ref('')
const selectedCode = ref<string | null>(null)
const visibleCount = ref(20)
const searchInput = ref<HTMLInputElement | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

// Debounced search
const debouncedQuery = ref('')
let searchTimeout: ReturnType<typeof setTimeout> | null = null

watch(searchQuery, (val) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedQuery.value = val
    visibleCount.value = 20
  }, 200)
})

// Initialize with profile country
watch(
  () => profileStore.profile?.country,
  (countryObj) => {
    if (countryObj) selectedCode.value = countryObj.code
  },
  { immediate: true }
)

const filteredCountries = computed(() => {
  const query = debouncedQuery.value.toLowerCase().trim()
  if (!query) return COUNTRIES
  
  return COUNTRIES.filter(c =>
    t(`countries.${c.code}`).toLowerCase().includes(query) ||
    c.nameEn.toLowerCase().includes(query) ||
    c.code.toLowerCase() === query
  )
})

function getFlagUrl(code: string): string {
  return `/assets/css/flags/flags/1x1/${code.toLowerCase()}.svg`
}

function onFlagError(e: Event) {
  const img = e.target as HTMLImageElement
  img.style.visibility = 'hidden'
}

async function selectCountry(country: CountryOption) {
  if (selectedCode.value === country.code) {
    emit('close')
    return
  }
  
  try {
    await profileStore.updateCountry(country.code)
    selectedCode.value = country.code
    emit('select', { code: country.code, name: country.name })
    emit('close')
  } catch (error) {
    console.error('Failed to save country:', error)
  }
}

// Infinite scroll
let observer: IntersectionObserver | null = null

function setupInfiniteScroll() {
  if (!loadMoreTrigger.value) return
  
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && visibleCount.value < filteredCountries.value.length) {
      visibleCount.value = Math.min(visibleCount.value + 20, filteredCountries.value.length)
    }
  }, { rootMargin: '100px' })
  
  observer.observe(loadMoreTrigger.value)
}

watch(() => props.visible, (visible) => {
  if (visible) {
    nextTick(() => {
      searchInput.value?.focus()
      setupInfiniteScroll()
    })
  } else {
    observer?.disconnect()
    searchQuery.value = ''
    visibleCount.value = 20
  }
})

onMounted(() => {
  if (props.visible) {
    setupInfiniteScroll()
  }
})

onUnmounted(() => {
  observer?.disconnect()
  if (searchTimeout) clearTimeout(searchTimeout)
})
</script>

<style scoped>
.country-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.country-modal {
  background: var(--hm-surface);
  border-radius: var(--p-radius-20);
  width: 320px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--hm-shadow-soft);
  animation: modalSlideIn 0.2s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.country-modal__header {
  padding: 12px 12px 8px;
}

.country-modal__search-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: var(--ds-overlay-06);
  border-radius: var(--ds-radius-card);
}

.country-modal__search-icon {
  width: 18px;
  height: 18px;
  color: var(--ds-overlay-40);
  flex-shrink: 0;
}

.country-modal__search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--hm-text-primary);
  font-size: var(--ds-text-size-body);
}

.country-modal__search-input::placeholder {
  color: var(--ds-overlay-40);
}

.country-modal__list {
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 8px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: var(--p-radius-lg);
  cursor: pointer;
  transition: background-color 0.15s;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
}

.country-item:hover {
  background: var(--ds-overlay-06);
}

.country-item--selected {
  background: var(--hm-accent-soft);
}

.country-item__flag {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--ds-overlay-10);
}

.country-item__name {
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-primary);
  font-weight: var(--ds-weight-medium);
}

.country-modal__load-more,
.country-modal__empty {
  padding: 16px;
  text-align: center;
  font-size: var(--p-text-13);
  color: var(--ds-overlay-40);
}

/* Custom scrollbar */
.country-modal__list::-webkit-scrollbar {
  width: 4px;
}

.country-modal__list::-webkit-scrollbar-track {
  background: transparent;
}

.country-modal__list::-webkit-scrollbar-thumb {
  background: var(--ds-overlay-15);
  border-radius: var(--p-radius-2);
}
</style>
